import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App.js';
import { useThemeStore } from './stores/themeStore.js';
import { startLivePermissions } from './stores/livePermissions.js';
import { VersionGate } from './components/VersionGate.js';
// One home for the pager's words — see components/common/PagerLabels.tsx. It
// wraps the router because a pager can appear on any route.
import { PagerLabels } from './components/common/PagerLabels.js';
// Import i18n at the entry so the singleton instance initialises before any
// component renders a useTranslation() hook. Also subscribe to language
// changes so `<html dir>` flips for RTL locales (Arabic) without requiring
// a reload. The v3+v4 audits flagged this import as the only thing the
// codebase needed to make its existing ~70KB i18n bundle do something.
import i18n, { applyDocumentLang, i18nReady } from './lib/i18n.js';
// Self-hosted webfonts — the warm-paper system's two families.
//
// Archivo (variable) carries headings and every number that matters; Instrument
// Sans carries UI text. They replace the single Roboto Flex variable face, which
// was here because the app was Material-flavoured — the design system is not any
// more, and one face cannot do both halves of the "loud numeral, quiet body"
// contrast the console is built on.
//
// Bundled via @fontsource so the app stays offline-friendly (no Google Fonts CDN
// call), which matters for the self-hosted cPanel target and for a foreman on
// site mobile data. DO NOT replace these with a <link> to fonts.googleapis.com.
// Imported before index.css so the @font-face rules are registered when our
// styles apply.
//
// Archivo is one variable file per subset, so one import covers 500/600/700.
// Instrument Sans is a static family, so each weight is its own import; the four
// below are exactly the weights docs/design-rules.md names for UI text, and
// importing the package root instead would ship only 400 and leave every
// `font-semibold` label to a synthesised fake bold.
import '@fontsource-variable/archivo';
import '@fontsource/instrument-sans/400.css';
import '@fontsource/instrument-sans/500.css';
import '@fontsource/instrument-sans/600.css';
import '@fontsource/instrument-sans/700.css';
import './index.css';

applyDocumentLang(i18n.language || 'en');
i18n.on('languageChanged', applyDocumentLang);

/* THE THEME BEFORE THE FIRST PAINT. `App` bootstraps it in an effect, and an
   effect runs AFTER the browser has painted — so a phone in dark mode drew one
   frame of the light palette on every load. Bootstrapping here, before React
   renders anything, puts `.dark` on <html> first. `bootstrap()` is idempotent,
   so the call in `App` stays as a harmless second read. */
useThemeStore.getState().bootstrap();

// The menu follows a role change without a sign-out (see the module).
startLivePermissions();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element missing');

/* After the catalogue (lib/i18n.ts, W-01) — and render even if it failed to
   load: keys on screen beat a blank page, and VersionGate can still offer the
   reload a stale deploy needs. */
void i18nReady
  .catch(() => undefined)
  .then(() => {
    ReactDOM.createRoot(rootEl).render(
      <React.StrictMode>
        <VersionGate>
          <PagerLabels>
            <App />
          </PagerLabels>
        </VersionGate>
      </React.StrictMode>
    );
  });

/**
 * Keep an already-open session current with the deployed build.
 *
 * The PWA registers with `registerType: 'autoUpdate'`, which swaps in a new
 * service worker — but only when the browser re-checks the SW script, which it
 * does on load, not while a tab (or an installed PWA) stays open. So after a
 * deploy, a long-lived session keeps serving the previously-cached bundle until
 * a manual hard refresh. That is the "I published but still see the old UI"
 * trap.
 *
 * This closes it: periodically (and on refocus / reconnect) ask the browser to
 * re-check for a new SW, and when a genuine UPDATE activates (a controller was
 * already in place — not the first install), reload the page once so it swaps
 * to the fresh assets. Production only; the dev server ships no SW.
 */
function setupServiceWorkerAutoUpdate(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  let updateReady = false;
  let reloaded = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only reload for a real update; the first install also changes the
    // controller but there is nothing stale to replace.
    if (!updateReady || reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  navigator.serviceWorker.ready
    .then((registration) => {
      const checkForUpdate = (): void => {
        void registration.update().catch(() => {
          /* offline or transient — the next tick retries */
        });
      };

      // Re-check every 30 minutes, whenever the tab regains focus, and when the
      // network comes back — so a publish is picked up without a manual refresh.
      window.setInterval(checkForUpdate, 30 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
      window.addEventListener('online', checkForUpdate);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // A worker reaching "installed" while a controller already exists is a
          // new build replacing the running one — mark it so controllerchange
          // reloads. A first-ever install has no controller here, so it won't.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            updateReady = true;
          }
        });
      });
    })
    .catch(() => {
      /* no service worker registration — nothing to keep fresh */
    });
}

setupServiceWorkerAutoUpdate();
